import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, Suspense } from "react";
import Layout from "./components/Layout";
import { initGA } from "./utils/analytics";
import { usePageTracking } from "./hooks/usePageTracking";
import { AnalyticsProvider } from "./components/AnalyticsProvider";
import { setupErrorHandlers } from "./utils/errorHandler";
import AdminRoute from "./components/AdminRoute";
import SignedInRoute from "./components/SignedInRoute";
import NotFound from "./components/NotFound";
import LegacyAnalysisRedirect from "./components/LegacyAnalysisRedirect";
import LegacyRapidReviewRedirect from "./components/LegacyRapidReviewRedirect";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import AppLoadingScreen from "./components/AppLoadingScreen";
import { getLiveDemoRedirect } from "./utils/liveDemoRedirect";
import {
  DatasetStudioFullPageSkeleton,
  datasetStudioSkeletonVariant,
  isDatasetStudioPath,
} from "./components/DatasetMarketplace/DatasetStudioPageSkeleton";

const RouteLoader = () => {
  const { pathname } = useLocation();
  if (isDatasetStudioPath(pathname)) {
    return (
      <DatasetStudioFullPageSkeleton variant={datasetStudioSkeletonVariant(pathname)} curatedCatalog={pathname.startsWith("/live-demo")} />
    );
  }
  return <AppLoadingScreen />;
};

const NomadicDocument = lazyWithRetry(() => import("./components/NomadicDocument"));
const StudioAutoAnalysisPage = lazyWithRetry(() => import("./components/BatchWorkbench/StudioAutoAnalysisPage"));
const VideosV2 = lazyWithRetry(() => import("./components/VideosV2"));
const AnalysisViewerPage = lazyWithRetry(() => import("./components/AnalysisViewerPage"));
const AuthPageShell = lazyWithRetry(() => import("./components/AuthPageShell"));
const LoginPage = lazyWithRetry(() => import("./components/LoginPage"));
const SignupPage = lazyWithRetry(() => import("./components/SignupPage"));
const CloudMarketplaceActivation = lazyWithRetry(
  () => import("./components/CloudMarketplaceActivation"),
);
const VerifyEmail = lazyWithRetry(() => import("./components/VerifyEmail"));
const Profile = lazyWithRetry(() => import("./components/ProfilePage"));
const Search = lazyWithRetry(() => import("./components/Search"));
const Examples = lazyWithRetry(() => import("./components/Examples"));
const DatasetMarketplace = lazyWithRetry(() => import("./components/DatasetMarketplace"));
const EmailActionHandler = lazyWithRetry(() => import("./components/EmailActionHandler"));
const RegularDrivingPage = lazyWithRetry(() => import("./pages/use-cases/regular-driving"));
const AutonomousSimulationPage = lazyWithRetry(() => import("./pages/use-cases/autonomous-simulation"));
const BusDrivingPage = lazyWithRetry(() => import("./pages/use-cases/bus-driving"));
const DronesPage = lazyWithRetry(() => import("./pages/use-cases/drones"));
const EdgeCaseDetectionPage = lazyWithRetry(() => import("./pages/use-cases/edge-case-detection"));
const RapidReviewPage = lazyWithRetry(() => import("./pages/use-cases/rapid-review"));
const BatchResultsViewer = lazyWithRetry(() => import("./pages/BatchResultsViewer"));
const BatchTokenUsageDashboard = lazyWithRetry(() => import("./pages/BatchTokenUsageDashboard"));
const CustomAgentsPage = lazyWithRetry(() => import("./pages/use-cases/CustomAgentsPage"));
const ViolationDetection = lazyWithRetry(() => import("./pages/ViolationDetection"));
const VisualInspectionPage = lazyWithRetry(() => import("./pages/use-cases/visual-inspection"));
const VideoComparisonPage = lazyWithRetry(() => import("./pages/use-cases/video-comparison"));
const LiveStreamDashboard = lazyWithRetry(() => import("./components/LiveStreamDashboard"));
const LiveStreamViewer = lazyWithRetry(() => import("./components/LiveStreamViewer"));
const MultiStreamViewer = lazyWithRetry(() => import("./components/MultiStreamViewer"));
const LiveSessionsList = lazyWithRetry(() => import("./pages/LiveSessionsList"));
const HistoryPage = lazyWithRetry(() => import("./pages/History"));
const WhatsNewPage = lazyWithRetry(() => import("./pages/WhatsNew"));
const SharedStructuredOutput = lazyWithRetry(() => import("./pages/SharedStructuredOutput"));
const TempCsvViewer = lazyWithRetry(() => import("./pages/TempCsvViewer"));
const EvaluationPage = lazyWithRetry(() => import("./pages/use-cases/EvaluationPage"));
const EvaluationCreatePage = lazyWithRetry(() => import("./pages/use-cases/EvaluationCreatePage"));
const EvaluationRunPage = lazyWithRetry(() => import("./pages/use-cases/EvaluationRunPage"));
const EvaluationComparePage = lazyWithRetry(() => import("./pages/use-cases/EvaluationComparePage"));
const DataCurationPage = lazyWithRetry(() => import("./pages/use-cases/DataCurationPage"));
const CompoundResultsViewer = lazyWithRetry(() => import("./pages/CompoundResultsViewer"));
const RouterV2Playground = lazyWithRetry(() => import("./pages/RouterV2Playground"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const MotionCoveragePage = lazyWithRetry(() => import("./pages/MotionCoveragePage"));
const StartPage2 = lazyWithRetry(() => import("./components/StartPage2"));
const StartPage3 = lazyWithRetry(() => import("./components/StartPage3"));
const AnalysisSession = lazyWithRetry(() => import("./pages/AnalysisSession"));
const BatchWorkbench = lazyWithRetry(
  () => import("./components/BatchWorkbench"),
);
const WorkbenchTaxonomyPage = lazyWithRetry(
  () => import("./components/BatchWorkbench/TaxonomyPage"),
);

/** `/dashboard` → `/` (or `/live-demo`) so there is one home-only entry. */
function DashboardPathRedirect() {
  const location = useLocation();
  const to =
    location.pathname.replace(/\/?dashboard\/?$/, "") || "/";
  return <Navigate to={to} replace />;
}

/** `/dataset-marketplace(/...)` → `/dataset-studio(/...)`. The route was
 *  renamed to match the "Dataset Studio" copy; old links (including the
 *  `/live-demo` prefixed ones) keep working. Preserves the dataset-id
 *  sub-path, the query string and the hash. */
function DatasetStudioPathRedirect() {
  const location = useLocation();
  const to =
    location.pathname.replace(
      /(^|\/)dataset-marketplace(?=\/|$)/,
      "$1dataset-studio",
    ) +
    location.search +
    location.hash;
  return <Navigate to={to} replace />;
}

/** The dataset start page is home for both authenticated and live-demo visitors. */
function HomeRoute() {
  return (
    <Layout>
      <StartPage3 />
    </Layout>
  );
}

const AppContent = () => {
  usePageTracking();
  const location = useLocation();
  const demoRedirect = getLiveDemoRedirect(location);

  if (demoRedirect) {
    return <Navigate to={demoRedirect} replace />;
  }

  return (
    <Suspense fallback={<RouteLoader />}>
    <Routes>
      {/* Auth-related routes (no /live-demo prefix) */}
      <Route element={<AuthPageShell />}>
        <Route path="login" element={<LoginPage />} />
        <Route path="signup" element={<SignupPage />} />
        <Route
          path="cloud-marketplace/activate"
          element={<CloudMarketplaceActivation />}
        />
      </Route>
      <Route path="verify-email" element={<VerifyEmail />} />
      <Route path="auth/action" element={<EmailActionHandler />} />
      {/* Main app routes (with and without /live-demo prefix) */}
      {["", "live-demo/"].map((prefix) => (
        <Route key={prefix} path={prefix}>
          <Route path="dashboard" element={<DashboardPathRedirect />} />
          <Route
            path="start-page-2"
            element={
              <Layout>
                <StartPage2 />
              </Layout>
            }
          />
          <Route
            path="start-page"
            element={
              <Layout>
                <StartPage3 />
              </Layout>
            }
          />
          <Route
            path="router-v2"
            element={
              <Layout>
                <RouterV2Playground />
              </Layout>
            }
          />
          <Route
            path="profile"
            element={
              <Layout>
                <Profile />
              </Layout>
            }
          />
          <Route index element={<HomeRoute />} />
          <Route
            path="documents"
            element={
              <Layout>
                <NomadicDocument />
              </Layout>
            }
          />
          <Route
            path="events"
            element={
              <Layout>
                <VideosV2 />
              </Layout>
            }
          />
          <Route
            path="video-explorer"
            element={
              <Layout>
                <VideosV2 />
              </Layout>
            }
          />
          <Route
            path="events/:videoId"
            element={
              <LegacyAnalysisRedirect>
                <Layout>
                  <AnalysisViewerPage />
                </Layout>
              </LegacyAnalysisRedirect>
            }
          />
          <Route
            path="events/:videoId/:analysisId"
            element={
              <LegacyAnalysisRedirect>
                <Layout>
                  <AnalysisViewerPage />
                </Layout>
              </LegacyAnalysisRedirect>
            }
          />
          <Route
            path="examples"
            element={
              <Layout>
                <Examples />
              </Layout>
            }
          />
          <Route
            path="whats-new"
            element={
              <SignedInRoute>
                <Layout>
                  <WhatsNewPage />
                </Layout>
              </SignedInRoute>
            }
          />
          {/* Revamped run view (new, lives alongside the old batch viewer). The
              query dashboard navigates here on Analyze; batchId is appended once
              the run has a batch. Wrapped in <Layout> for the app's left nav; on
              desktop SideBar renders only the left rail (no top header), so the
              page's own single header is the only one. */}
          <Route
            path="dashboard-2/run"
            element={
              <AdminRoute>
                <Layout>
                  <AnalysisSession />
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path="dashboard-2/run/:batchId"
            element={
              <AdminRoute>
                <Layout>
                  <AnalysisSession />
                </Layout>
              </AdminRoute>
            }
          />
          {/* Workbench is customer-facing, not admin-only: the backend
              enforces per-batch access (owner / org ACL / share token) on
              every workbench endpoint, so the route guard here matches the
              dataset pages' pattern — Layout only. A batch the caller can't
              read still 404s server-side. */}
          <Route path="workbench/new-analysis" element={<Layout contentKey="workbench"><StudioAutoAnalysisPage /></Layout>} />
          <Route
            path="workbench/videos/:videoId"
            element={
              <Layout>
                <BatchWorkbench />
              </Layout>
            }
          />
          <Route
            path="workbench/preparing/:streamId"
            element={
              <Layout contentKey="workbench">
                <BatchWorkbench />
              </Layout>
            }
          />
          <Route
            path="workbench/:batchId"
            element={
              // Workbench reconciles query/family changes in its providers.
              // Keep them mounted across sibling queries and preserve layout.
              <Layout contentKey="workbench">
                <BatchWorkbench />
              </Layout>
            }
          />
          <Route
            path="workbench/:batchId/tokens"
            element={
              <Layout>
                <BatchTokenUsageDashboard workbench />
              </Layout>
            }
          />
          <Route
            path="workbench/:batchId/taxonomy"
            element={
              <Layout>
                <WorkbenchTaxonomyPage />
              </Layout>
            }
          />
          <Route
            path="dataset-studio"
            element={
              <Layout>
                <DatasetMarketplace />
              </Layout>
            }
          />
          <Route
            path="dataset-studio/:datasetId/distribution-new"
            element={
              <Layout>
                <DatasetMarketplace />
              </Layout>
            }
          />
          <Route
            path="dataset-studio/:datasetId"
            element={
              <Layout>
                <DatasetMarketplace />
              </Layout>
            }
          />
          {/* Retired route name — the page is /dataset-studio since the copy
              was renamed. Kept so existing external links still land. */}
          <Route
            path="dataset-marketplace"
            element={<DatasetStudioPathRedirect />}
          />
          <Route
            path="dataset-marketplace/*"
            element={<DatasetStudioPathRedirect />}
          />
          <Route
            path="search/:videoId/:eventId"
            element={
              <Layout>
                <Search />
              </Layout>
            }
          />
          <Route
            path="search"
            element={
              <Layout>
                <Search />
              </Layout>
            }
          />
          <Route
            path="history"
            element={
              prefix === "live-demo/" ? (
                <Navigate to="/live-demo" replace />
              ) : (
                <Layout>
                  <HistoryPage />
                </Layout>
              )
            }
          />
          <Route
            path="live-streams"
            element={
              <Layout>
                <MultiStreamViewer />
              </Layout>
            }
          />
          <Route
            path="live-streams/dashboard"
            element={
              <Layout>
                <LiveStreamDashboard />
              </Layout>
            }
          />
          <Route
            path="live-streams/:sessionId"
            element={
              <Layout>
                <LiveStreamViewer />
              </Layout>
            }
          />
          <Route
            path="live-sessions"
            element={
              <Layout>
                <LiveSessionsList />
              </Layout>
            }
          />
          {/* Use Case Routes */}
          <Route
            path="use-cases/violation-detection"
            element={
              <Layout>
                <ViolationDetection />
              </Layout>
            }
          />
          <Route
            path="use-cases/edge-case-detection"
            element={
              <Layout>
                <EdgeCaseDetectionPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/rapid-review"
            element={
              <Layout>
                <RapidReviewPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/rapid-review/batch"
            element={
              <Layout>
                <VideosV2 />
              </Layout>
            }
          />
          <Route
            path="use-cases/rapid-review/batch-view/:batchId/tokens"
            element={
              <Layout>
                <BatchTokenUsageDashboard />
              </Layout>
            }
          />
          <Route
            path="use-cases/rapid-review/batch-view/:batchId"
            element={
              <Layout>
                <BatchResultsViewer />
              </Layout>
            }
          />
          <Route
            path="use-cases/rapid-review/compound/:compoundId"
            element={
              <Layout>
                <CompoundResultsViewer />
              </Layout>
            }
          />
          {/* Compatibility route for retired Rapid Review viewer URLs */}
          <Route
            path="use-cases/rapid-review/view/:videoId/:analysisId?"
            element={<LegacyRapidReviewRedirect />}
          />
          <Route
            path="use-cases/custom-agents"
            element={
              <Layout>
                <CustomAgentsPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/regular-driving"
            element={
              <Layout>
                <RegularDrivingPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/autonomous-simulation"
            element={
              <Layout>
                <AutonomousSimulationPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/bus-driving"
            element={
              <Layout>
                <BusDrivingPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/drones"
            element={
              <Layout>
                <DronesPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/visual-inspection"
            element={
              <Layout>
                <VisualInspectionPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/video-comparison"
            element={
              <Layout>
                <VideoComparisonPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/statistics"
            element={
              <Navigate
                to={prefix === "live-demo/" ? "/live-demo" : "/"}
                replace
              />
            }
          />
          <Route
            path="analytics"
            element={
              <Navigate
                to={prefix === "live-demo/" ? "/live-demo" : "/"}
                replace
              />
            }
          />
          <Route
            path="use-cases/statistics/generate"
            element={
              <Navigate
                to={prefix === "live-demo/" ? "/live-demo" : "/"}
                replace
              />
            }
          />
          <Route
            path="use-cases/evaluation"
            element={
              <Layout>
                <EvaluationPage />
              </Layout>
            }
          />
          <Route
            path="use-cases/evaluation/create"
            element={
              <Layout>
                <EvaluationCreatePage />
              </Layout>
            }
          />
          <Route
            path="use-cases/evaluation/compare"
            element={
              <Layout>
                <EvaluationComparePage />
              </Layout>
            }
          />
          <Route
            path="use-cases/data-curation"
            element={
              <AdminRoute>
                <Layout>
                  <DataCurationPage />
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path="use-cases/evaluation/:jobId/runs/:runId"
            element={
              <Layout>
                <EvaluationRunPage />
              </Layout>
            }
          />
          <Route
            path="shared/structured-output/:shareId"
            element={<SharedStructuredOutput />}
          />
          <Route path="shared/csv-data" element={<TempCsvViewer />} />
        </Route>
      ))}

      <Route
        path="admin/dashboard"
        element={
          <AdminRoute firebaseOnly>
            <Layout>
              <AdminDashboard />
            </Layout>
          </AdminRoute>
        }
      />
      {/* No AdminRoute: the motion-coverage API decides (admins and internal orgs), so internal users who
          start a run can open its page; everyone else sees the API's refusal. */}
      {["motion-coverage", "motion-coverage/:jobId"].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <Layout>
              <MotionCoveragePage />
            </Layout>
          }
        />
      ))}

      {/* Public shared routes (no authentication required) */}
      <Route
        path="shared/structured-output/:shareId"
        element={<SharedStructuredOutput />}
      />
      <Route path="shared/csv-data" element={<TempCsvViewer />} />

      {/* Catch-all route for 404 pages */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

const App = () => {
  useEffect(() => {
    initGA();
    setupErrorHandlers();
  }, []);

  return (
    <Router>
      <AnalyticsProvider>
        <AppContent />
      </AnalyticsProvider>
    </Router>
  );
};

export default App;
